import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Progress,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title, RingProgress
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBuildingFactory2,
  IconMapPin,
  IconPhone,
  IconReceipt2,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
  IconStar,
  IconUsers, IconPlus, IconArrowLeft
} from '@tabler/icons-react';

const suppliersSeed = [
  {
    id: 'SUP-001',
    name: 'OfisDonanım Ltd.',
    category: 'IT Ekipman',
    city: 'İstanbul',
    contactPerson: 'Merve Aydın',
    phone: '0212 555 14 22',
    email: 'teklif@ofisdonanim.com',
    status: 'ACTIVE',
    score: 91,
    totalSpend: 428000,
    totalQuotes: 46,
    winRate: 41,
    avgLeadTime: 4,
    onTimeRate: 96,
    returnRate: 2.1,
    invoiceMatchRate: 98,
    activeContracts: 3,
    tags: ['Anlaşmalı', 'Hızlı Teslim', 'Kurumsal'],
  },
  {
    id: 'SUP-002',
    name: 'Anadolu Endüstri',
    category: 'İSG / Saha Ekipman',
    city: 'Ankara',
    contactPerson: 'Serkan Uslu',
    phone: '0312 444 88 10',
    email: 'satis@anadoluendustri.com',
    status: 'RISKY',
    score: 72,
    totalSpend: 352000,
    totalQuotes: 33,
    winRate: 28,
    avgLeadTime: 6,
    onTimeRate: 89,
    returnRate: 4.8,
    invoiceMatchRate: 92,
    activeContracts: 1,
    tags: ['Saha Tedarikçisi', 'İade Takibi'],
  },
  {
    id: 'SUP-003',
    name: 'Tekno Tedarik A.Ş.',
    category: 'Elektronik / IT',
    city: 'İzmir',
    contactPerson: 'Cem Korkmaz',
    phone: '0232 401 77 55',
    email: 'kurumsal@teknotedarik.com',
    status: 'ACTIVE',
    score: 84,
    totalSpend: 301000,
    totalQuotes: 39,
    winRate: 22,
    avgLeadTime: 5,
    onTimeRate: 93,
    returnRate: 1.9,
    invoiceMatchRate: 95,
    activeContracts: 2,
    tags: ['Alternatif Tedarikçi', 'Elektronik'],
  },
  {
    id: 'SUP-004',
    name: 'Kurumsal Market',
    category: 'Ofis Sarf',
    city: 'Bursa',
    contactPerson: 'Derya Şimşek',
    phone: '0224 300 19 19',
    email: 'info@kurumsalmarket.com',
    status: 'PASSIVE',
    score: 68,
    totalSpend: 188000,
    totalQuotes: 18,
    winRate: 17,
    avgLeadTime: 3,
    onTimeRate: 91,
    returnRate: 3.7,
    invoiceMatchRate: 90,
    activeContracts: 0,
    tags: ['Sarf Malzeme'],
  },
];

const currency = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 0,
});

const statusMap = {
  ACTIVE: { label: 'Aktif', color: 'teal' },
  RISKY: { label: 'Riskli', color: 'orange' },
  PASSIVE: { label: 'Pasif', color: 'gray' },
  BLOCKED: { label: 'Bloke', color: 'red' },
};

function StatusBadge({ status }) {
  const cfg = statusMap[status] || { label: status, color: 'gray' };
  return (
    <Badge color={cfg.color} variant="light" radius="sm">
      {cfg.label}
    </Badge>
  );
}

function KpiCard({ title, value, subtitle, color, icon: Icon }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>{title}</Text>
          <Text fw={800} size="1.55rem" lh={1.1}>{value}</Text>
          <Text size="xs" c="dimmed">{subtitle}</Text>
        </Stack>
        <ThemeIcon color={color} variant="light" size={46} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={4}>{title}</Title>
          {description ? <Text size="sm" c="dimmed" mt={4}>{description}</Text> : null}
        </div>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">{label}</Text>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Text size="sm" fw={600} ta="right">{value || '-'}</Text>
        ) : (
          value
        )}
      </Group>
      <Divider />
    </>
  );
}

export default function SupplierNetworkPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedSupplierId, setSelectedSupplierId] = useState(suppliersSeed[0].id);

  const filteredSuppliers = useMemo(() => {
    return suppliersSeed.filter((supplier) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q || JSON.stringify(supplier).toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'ALL' ? true : supplier.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [search, statusFilter]);

  const selectedSupplier = useMemo(
    () => suppliersSeed.find((s) => s.id === selectedSupplierId) || suppliersSeed[0],
    [selectedSupplierId]
  );

  const stats = useMemo(() => {
    const activeCount = suppliersSeed.filter((s) => s.status === 'ACTIVE').length;
    const riskyCount = suppliersSeed.filter((s) => s.status === 'RISKY').length;
    const totalSpend = suppliersSeed.reduce((acc, s) => acc + s.totalSpend, 0);
    const avgScore = Math.round(
      suppliersSeed.reduce((acc, s) => acc + s.score, 0) / suppliersSeed.length
    );
    return { activeCount, riskyCount, totalSpend, avgScore };
  }, []);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          withBorder
          radius="xl"
          p="xl"
          shadow="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xl">
            <div>
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/purchase-dashboard")}
                >
                Geri
              </Button>
              <Badge variant="light" color="cyan" leftSection={<IconBuildingFactory2 size={14} />}>
                Tedarikçi Ağı Yönetimi
              </Badge>
              <Title order={1} mt="md">Tedarikçi Ağı</Title>
              <Text size="sm" c="dimmed" mt="sm" maw={900}>
                Tedarikçilerin performans, iletişim, teslimat kalitesi, teklif başarısı ve finansal uyum açısından tek ekranda izlendiği yönetim sayfası.
              </Text>
            </div>
            <Group>
              <Select
                w={160}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || 'ALL')}
                data={[
                  { value: 'ALL', label: 'Tüm Durumlar' },
                  { value: 'ACTIVE', label: 'Aktif' },
                  { value: 'RISKY', label: 'Riskli' },
                  { value: 'PASSIVE', label: 'Pasif' },
                ]}
              />
                <Button
                    color="cyan"
                    leftSection={<IconPlus size={16} />}
                >
                    Yeni Tedarikçi
                </Button>

              <Button variant="default" leftSection={<IconRefresh size={16} />}>Yenile</Button>
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md" mt="xl">
            <KpiCard title="Aktif Tedarikçi" value={stats.activeCount} subtitle="Düzenli çalışan ağ" color="teal" icon={IconShieldCheck} />
            <KpiCard title="Riskli Tedarikçi" value={stats.riskyCount} subtitle="Kalite / termin takibi gerekli" color="orange" icon={IconAlertTriangle} />
            <KpiCard title="Toplam Hacim" value={currency.format(stats.totalSpend)} subtitle="Tedarikçi bazlı toplam harcama" color="blue" icon={IconReceipt2} />
            <KpiCard title="Ortalama Skor" value={stats.avgScore} subtitle="Performans puanı ortalaması" color="grape" icon={IconStar} />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 3.5 }}>
            <Stack gap="md">
              <SectionCard title="Tedarikçi Arama" description="Ağ içindeki tedarikçileri hızlı filtreleyin.">
                <Stack gap="sm">
                  <TextInput
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    placeholder="Firma, kişi, kategori, şehir ara..."
                    leftSection={<IconSearch size={16} />}
                  />

                  <Stack gap="xs">
                    {filteredSuppliers.map((supplier) => (
                      <Paper
                        key={supplier.id}
                        withBorder
                        radius="md"
                        p="sm"
                        onClick={() => setSelectedSupplierId(supplier.id)}
                        style={{
                          cursor: 'pointer',
                          borderColor: selectedSupplierId === supplier.id ? 'var(--mantine-color-cyan-4)' : undefined,
                          backgroundColor: selectedSupplierId === supplier.id ? 'var(--mantine-color-cyan-0)' : undefined,
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap" align="flex-start">
                            <Avatar radius="xl" color="cyan">
                              {supplier.name.charAt(0)}
                            </Avatar>
                            <div>
                              <Text fw={700} size="sm">{supplier.name}</Text>
                              <Text size="xs" c="dimmed" mt={4}>{supplier.category}</Text>
                              <Text size="xs" c="dimmed">{supplier.city}</Text>
                            </div>
                          </Group>
                          <Stack gap={6} align="flex-end">
                            <StatusBadge status={supplier.status} />
                            <Badge variant="light" color="violet">Skor {supplier.score}</Badge>
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard title="Tedarikçi Özeti" description="Seçilen kaydın hızlı görünümü.">
                {selectedSupplier && (
                  <Stack gap="sm">
                    <InfoRow label="Firma" value={selectedSupplier.name} />
                    <InfoRow label="Kategori" value={selectedSupplier.category} />
                    <InfoRow label="Şehir" value={selectedSupplier.city} />
                    <InfoRow label="Durum" value={<StatusBadge status={selectedSupplier.status} />} />
                    <InfoRow label="İletişim Kişisi" value={selectedSupplier.contactPerson} />
                    <InfoRow label="Telefon" value={selectedSupplier.phone} />
                    <InfoRow label="E-posta" value={selectedSupplier.email} />
                    <InfoRow label="Aktif Sözleşme" value={selectedSupplier.activeContracts} />
                  </Stack>
                )}
              </SectionCard>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 8.5 }}>
            <Stack gap="lg">
              {selectedSupplier && (
                <>
                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Performans Göstergeleri" description="Seçili tedarikçinin operasyonel ve finansal performansı.">
                      <Stack gap="sm">
                        <InfoRow label="Toplam Harcama" value={currency.format(selectedSupplier.totalSpend)} />
                        <InfoRow label="Toplam Teklif" value={selectedSupplier.totalQuotes} />
                        <InfoRow label="Kazanma Oranı" value={`%${selectedSupplier.winRate}`} />
                        <InfoRow label="Ortalama Termin" value={`${selectedSupplier.avgLeadTime} gün`} />
                        <InfoRow label="Zamanında Teslim" value={`%${selectedSupplier.onTimeRate}`} />
                        <InfoRow label="İade Oranı" value={`%${selectedSupplier.returnRate}`} />
                        <InfoRow label="Fatura Eşleşme Oranı" value={`%${selectedSupplier.invoiceMatchRate}`} />
                        <InfoRow label="Skor" value={<Badge color="violet" variant="light">{selectedSupplier.score}</Badge>} />
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Kalite ve Uyum" description="KPI bazlı hızlı kalite değerlendirmesi.">
                      <Stack gap="md">
                        <div>
                          <Group justify="space-between" mb={6}>
                            <Text size="sm" c="dimmed">Zamanında Teslim</Text>
                            <Text size="sm" fw={700}>%{selectedSupplier.onTimeRate}</Text>
                          </Group>
                          <Progress value={selectedSupplier.onTimeRate} size="lg" radius="xl" color="teal" />
                        </div>
                        <div>
                          <Group justify="space-between" mb={6}>
                            <Text size="sm" c="dimmed">Fatura Eşleşmesi</Text>
                            <Text size="sm" fw={700}>%{selectedSupplier.invoiceMatchRate}</Text>
                          </Group>
                          <Progress value={selectedSupplier.invoiceMatchRate} size="lg" radius="xl" color="blue" />
                        </div>
                        <div>
                          <Group justify="space-between" mb={6}>
                            <Text size="sm" c="dimmed">İade Riski</Text>
                            <Text size="sm" fw={700}>%{selectedSupplier.returnRate}</Text>
                          </Group>
                          <Progress value={Math.min(selectedSupplier.returnRate * 10, 100)} size="lg" radius="xl" color="orange" />
                        </div>

                        <Paper withBorder radius="md" p="sm" bg="gray.0">
                          <Group justify="space-between" align="center">
                            <Text size="sm" c="dimmed">Genel Ağ Skoru</Text>
                            <RingProgress
                              size={70}
                              thickness={8}
                              roundCaps
                              sections={[{ value: selectedSupplier.score, color: 'violet' }]}
                              label={<Text size="xs" fw={800} ta="center">{selectedSupplier.score}</Text>}
                            />
                          </Group>
                        </Paper>
                      </Stack>
                    </SectionCard>
                  </SimpleGrid>

                  <SectionCard title="Tedarikçi Kartı" description="Kurumsal profil, temas bilgileri ve etiketler.">
                    <Group align="flex-start" gap="xl">
                      <Avatar size={84} radius="xl" color="cyan">
                        {selectedSupplier.name.charAt(0)}
                      </Avatar>

                      <Stack gap="sm" style={{ flex: 1 }}>
                        <Group justify="space-between" align="center">
                          <div>
                            <Title order={3}>{selectedSupplier.name}</Title>
                            <Text size="sm" c="dimmed" mt={4}>{selectedSupplier.category}</Text>
                          </div>
                          <Group>
                            <StatusBadge status={selectedSupplier.status} />
                            <Badge variant="light" color="violet">Skor {selectedSupplier.score}</Badge>
                          </Group>
                        </Group>

                        <Group gap="md">
                          <Group gap={6}><IconUsers size={16} /><Text size="sm">{selectedSupplier.contactPerson}</Text></Group>
                          <Group gap={6}><IconPhone size={16} /><Text size="sm">{selectedSupplier.phone}</Text></Group>
                          <Group gap={6}><IconMapPin size={16} /><Text size="sm">{selectedSupplier.city}</Text></Group>
                        </Group>

                        <Group gap="xs">
                          {selectedSupplier.tags.map((tag) => (
                            <Badge key={tag} variant="light">{tag}</Badge>
                          ))}
                        </Group>
                      </Stack>
                    </Group>
                  </SectionCard>
                </>
              )}

              <SectionCard title="Tedarikçi Kıyas Tablosu" description="Ağdaki ana tedarikçilerin karşılaştırmalı görünümü.">
                <ScrollArea>
                  <Table highlightOnHover striped verticalSpacing="sm" horizontalSpacing="md">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Firma</Table.Th>
                        <Table.Th>Harcama</Table.Th>
                        <Table.Th>Termin</Table.Th>
                        <Table.Th>Zamanında Teslim</Table.Th>
                        <Table.Th>İade Oranı</Table.Th>
                        <Table.Th>Kazanma</Table.Th>
                        <Table.Th>Durum</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {suppliersSeed.map((supplier) => (
                        <Table.Tr key={supplier.id}>
                          <Table.Td>
                            <Text fw={700}>{supplier.name}</Text>
                          </Table.Td>
                          <Table.Td>{currency.format(supplier.totalSpend)}</Table.Td>
                          <Table.Td>{supplier.avgLeadTime} gün</Table.Td>
                          <Table.Td>%{supplier.onTimeRate}</Table.Td>
                          <Table.Td>%{supplier.returnRate}</Table.Td>
                          <Table.Td>%{supplier.winRate}</Table.Td>
                          <Table.Td><StatusBadge status={supplier.status} /></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </SectionCard>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
